import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Stack,
  Checkbox,
  Pagination,
  Card,
  CardContent,
  Grid,
  Divider,
  Link
} from '@mui/material';
import {
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Error as ErrorIcon,
  Pending as PendingIcon,
  Done as DoneIcon,
  Delete as DeleteIcon,
  OpenInNew as OpenInNewIcon,
  Link as LinkIcon,
  Person as PersonIcon,
  RestartAlt as RestartAltIcon
} from '@mui/icons-material';
import api from '../api';
import { useAuth } from '../AuthContext';
import MoaniDisplay from './MoaniDisplay';

function MoaniBlockchainTransfers() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  // State for pagination and filters
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({
    status: '',
    direction: '',
    chainId: '',
    search: ''
  });
  
  // State for dialogs
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [bulkStatusDialogOpen, setBulkStatusDialogOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [selectedTransactions, setSelectedTransactions] = useState([]);
  
  // State for forms
  const [statusForm, setStatusForm] = useState({
    status: '',
    errorMessage: '',
    notes: ''
  });
  
  const [editForm, setEditForm] = useState({
    status: '',
    retryCount: 0,
    maxRetries: 3,
    errorMessage: '',
    txHash: '',
    blockNumber: '',
    confirmationBlocks: 0,
    requiredConfirmations: 12,
    chainId: '',
    contractAddress: '',
    userWallet: '',
    systemWallet: '',
    amount: ''
  });
  
  const [alerts, setAlerts] = useState([]);
  
  const showAlert = (message, severity = 'info') => {
    const alert = { id: Date.now(), message, severity };
    setAlerts(prev => [...prev, alert]);
    setTimeout(() => {
      setAlerts(prev => prev.filter(a => a.id !== alert.id));
    }, 5000);
  };

  // Fetch blockchain transfers
  const { data: transfersData, isLoading, error, refetch } = useQuery({
    queryKey: ['blockchainTransfers', page, filters],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '50',
        ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v))
      });
      
      const response = await api.get(`/moani-blockchain-transfers?${params}`);
      return response.data;
    },
    enabled: ['manager', 'admin', 'super_admin'].includes(user?.role)
  });

  // Fetch filter options
  const { data: filterOptions } = useQuery({
    queryKey: ['blockchainTransferFilters'],
    queryFn: async () => {
      const response = await api.get('/moani-blockchain-transfers/filters/options');
      return response.data;
    },
    enabled: ['manager', 'admin', 'super_admin'].includes(user?.role)
  });

  // Update status mutation
  const updateStatusMutation = useMutation({
    mutationFn: async ({ transactionId, statusData }) => {
      console.log('Making API call:', transactionId, statusData);
      const response = await api.put(`/moani-blockchain-transfers/${transactionId}/status`, statusData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['blockchainTransfers']);
      setStatusDialogOpen(false);
      setSelectedTransaction(null);
      setStatusForm({ status: '', errorMessage: '', notes: '' });
      showAlert('Transaction status updated successfully', 'success');
    },
    onError: (error) => {
      showAlert(error.response?.data?.error || 'Failed to update transaction status', 'error');
    }
  });

  // Update transaction mutation (full edit)
  const updateTransactionMutation = useMutation({
    mutationFn: async ({ transactionId, transactionData }) => {
      const response = await api.put(`/moani-blockchain-transfers/${transactionId}`, transactionData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['blockchainTransfers']);
      setStatusDialogOpen(false);
      setSelectedTransaction(null);
      showAlert('Transaction updated successfully', 'success');
    },
    onError: (error) => {
      showAlert(error.response?.data?.error || 'Failed to update transaction', 'error');
    }
  });

  // Bulk update status mutation
  const bulkUpdateStatusMutation = useMutation({
    mutationFn: async (statusData) => {
      const response = await api.put('/moani-blockchain-transfers/bulk/status', statusData);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(['blockchainTransfers']);
      setBulkStatusDialogOpen(false);
      setSelectedTransactions([]);
      setStatusForm({ status: '', errorMessage: '', notes: '' });
      showAlert(`Successfully updated ${data.affectedRows} transactions`, 'success');
    },
    onError: (error) => {
      showAlert(error.response?.data?.error || 'Failed to bulk update transactions', 'error');
    }
  });

  // Delete transaction mutation
  const deleteTransactionMutation = useMutation({
    mutationFn: async (transactionId) => {
      const response = await api.delete(`/moani-blockchain-transfers/${transactionId}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['blockchainTransfers']);
      showAlert('Transaction deleted successfully', 'success');
    },
    onError: (error) => {
      showAlert(error.response?.data?.error || 'Failed to delete transaction', 'error');
    }
  });

  // Reset transaction mutation
  const resetTransactionMutation = useMutation({
    mutationFn: async (transactionId) => {
      const response = await api.post(`/moani-blockchain-transfers/${transactionId}/reset`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['blockchainTransfers']);
      showAlert('Transaction reset successfully - retry count set to 0 and status set to PENDING', 'success');
    },
    onError: (error) => {
      showAlert(error.response?.data?.error || 'Failed to reset transaction', 'error');
    }
  });

  const handleStatusUpdate = (transaction) => {
    setSelectedTransaction(transaction);
    // Populate edit form with all transaction fields
    setEditForm({
      status: transaction.status || '',
      retryCount: transaction.retryCount || 0,
      maxRetries: transaction.maxRetries || 3,
      errorMessage: transaction.errorMessage || '',
      txHash: transaction.txHash || '',
      blockNumber: transaction.blockNumber || '',
      confirmationBlocks: transaction.confirmationBlocks || 0,
      requiredConfirmations: transaction.requiredConfirmations || 12,
      chainId: transaction.chainId || '',
      contractAddress: transaction.contractAddress || '',
      userWallet: transaction.userWallet || '',
      systemWallet: transaction.systemWallet || '',
      amount: transaction.amount || ''
    });
    setStatusDialogOpen(true);
  };

  const handleDeleteTransaction = (transactionId) => {
    if (window.confirm('Are you sure you want to delete this blockchain transfer entry? This action cannot be undone.')) {
      deleteTransactionMutation.mutate(transactionId);
    }
  };

  const handleResetTransaction = (transactionId) => {
    if (window.confirm('Reset this transaction? This will set retry count to 0 and status to PENDING, allowing it to be processed again.')) {
      resetTransactionMutation.mutate(transactionId);
    }
  };

  const handleBulkStatusUpdate = () => {
    if (selectedTransactions.length === 0) {
      showAlert('Please select transactions to update', 'warning');
      return;
    }
    setStatusForm({ status: '', errorMessage: '', notes: '' });
    setBulkStatusDialogOpen(true);
  };

  const handleSubmitStatusUpdate = () => {
    // Use the new full edit mutation
    updateTransactionMutation.mutate({
      transactionId: selectedTransaction.id,
      transactionData: editForm
    });
  };
  
  const getSnowtraceUrl = (txHash) => {
    if (!txHash || txHash === 'none' || txHash === 'null') return null;
    return `https://snowtrace.io/tx/${txHash}`;
  };
  
  const getUserUrl = (userId) => {
    if (!userId) return null;
    return `/user/${userId}/details`;
  };
  
  const formatAmount = (amount) => {
    if (!amount) return '0';
    try {
      const amountBigInt = BigInt(amount);
      const divisor = BigInt(10 ** 18);
      return (Number(amountBigInt) / Number(divisor)).toFixed(6);
    } catch {
      return amount;
    }
  };

  const handleSubmitBulkStatusUpdate = () => {
    if (!statusForm.status) {
      showAlert('Please select a status', 'error');
      return;
    }

    bulkUpdateStatusMutation.mutate({
      transactionIds: selectedTransactions,
      ...statusForm
    });
  };

  const handleSelectTransaction = (transactionId, checked) => {
    if (checked) {
      setSelectedTransactions(prev => [...prev, transactionId]);
    } else {
      setSelectedTransactions(prev => prev.filter(id => id !== transactionId));
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      const allIds = transfersData?.transactions?.map(tx => tx.id) || [];
      setSelectedTransactions(allIds);
    } else {
      setSelectedTransactions([]);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'CONFIRMED': return <CheckCircleIcon />;
      case 'PROCESSED': return <DoneIcon />;
      case 'FAILED': return <ErrorIcon />;
      case 'CANCELLED': return <CancelIcon />;
      default: return <PendingIcon />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'CONFIRMED': return 'info';
      case 'PROCESSED': return 'success';
      case 'FAILED': return 'error';
      case 'CANCELLED': return 'warning';
      default: return 'default';
    }
  };

  const getDirectionColor = (direction) => {
    return direction === 'DEPOSIT' ? 'success' : 'info';
  };

  const formatDate = (dateString) => {
    return dateString ? new Date(dateString).toLocaleString() : 'N/A';
  };

  const formatAddress = (address) => {
    return address ? `${address.slice(0, 6)}...${address.slice(-4)}` : 'N/A';
  };

  if (!user || !['manager', 'admin', 'super_admin'].includes(user.role)) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <Typography variant="h6">Access denied. Manager privileges or higher required.</Typography>
      </Box>
    );
  }

  const transactions = transfersData?.transactions || [];
  const pagination = transfersData?.pagination || {};
  const summary = transfersData?.summary || [];

  return (
    <Box>
      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <Typography variant="h4" component="div" flexGrow={1}>
          MOANI Blockchain Transfers
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => refetch()}
          disabled={isLoading}
        >
          Refresh
        </Button>
        {selectedTransactions.length > 0 && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleBulkStatusUpdate}
          >
            Update Selected ({selectedTransactions.length})
          </Button>
        )}
      </Stack>

      {alerts.map(alert => (
        <Alert key={alert.id} severity={alert.severity} sx={{ mb: 2 }}>
          {alert.message}
        </Alert>
      ))}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load blockchain transfers: {error.message}
        </Alert>
      )}

      {/* Summary Cards */}
      {summary.length > 0 && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {summary.map((item) => (
            <Grid item xs={12} sm={6} md={3} key={item.status}>
              <Card>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    {getStatusIcon(item.status)}
                    <Box>
                      <Typography variant="h6">{item.count}</Typography>
                      <Typography color="text.secondary" variant="body2">
                        {item.status}
                      </Typography>
                      {item.totalAmount && (
                        <MoaniDisplay 
                          amount={item.totalAmount} 
                          isWei={true}
                          variant="body2"
                        />
                      )}
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Search"
              placeholder="Hash, wallet, user..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                label="Status"
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              >
                <MenuItem value="">All</MenuItem>
                {filterOptions?.statuses?.map((status) => (
                  <MenuItem key={status.status} value={status.status}>
                    {status.status} ({status.count})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Direction</InputLabel>
              <Select
                value={filters.direction}
                label="Direction"
                onChange={(e) => setFilters(prev => ({ ...prev, direction: e.target.value }))}
              >
                <MenuItem value="">All</MenuItem>
                {filterOptions?.directions?.map((direction) => (
                  <MenuItem key={direction.direction} value={direction.direction}>
                    {direction.direction} ({direction.count})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Chain</InputLabel>
              <Select
                value={filters.chainId}
                label="Chain"
                onChange={(e) => setFilters(prev => ({ ...prev, chainId: e.target.value }))}
              >
                <MenuItem value="">All</MenuItem>
                {filterOptions?.chains?.map((chain) => (
                  <MenuItem key={chain.chainId} value={chain.chainId}>
                    Chain {chain.chainId} ({chain.count})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setFilters({ status: '', direction: '', chainId: '', search: '' })}
              >
                Clear Filters
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {/* Transactions Table */}
      <Paper sx={{ p: 3 }}>
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedTransactions.length === transactions.length && transactions.length > 0}
                        indeterminate={selectedTransactions.length > 0 && selectedTransactions.length < transactions.length}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                      />
                    </TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>Direction</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Chain</TableCell>
                    <TableCell>Transaction Hash</TableCell>
                    <TableCell>Wallets</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transactions.map((transaction) => (
                    <TableRow key={transaction.id}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedTransactions.includes(transaction.id)}
                          onChange={(e) => handleSelectTransaction(transaction.id, e.target.checked)}
                        />
                      </TableCell>
                      <TableCell>
                        {transaction.userId ? (
                          <Button
                            size="small"
                            variant="text"
                            href={getUserUrl(transaction.userId)}
                            target="_blank"
                            rel="noopener noreferrer"
                            startIcon={<PersonIcon fontSize="small" />}
                            sx={{ textTransform: 'none', justifyContent: 'flex-start' }}
                          >
                            <Box>
                              <Typography variant="body2" fontWeight="bold">
                                {transaction.userName || 'Unknown User'}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {transaction.userEmail}
                              </Typography>
                            </Box>
                          </Button>
                        ) : (
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {transaction.userName || 'Unknown User'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {transaction.userEmail}
                            </Typography>
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={transaction.direction}
                          size="small"
                          color={getDirectionColor(transaction.direction)}
                          variant="filled"
                        />
                      </TableCell>
                      <TableCell>
                        <MoaniDisplay 
                          amount={transaction.amount} 
                          isWei={true}
                          variant="body2"
                          sx={{ fontFamily: 'monospace' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={getStatusIcon(transaction.status)}
                          label={transaction.status}
                          size="small"
                          color={getStatusColor(transaction.status)}
                          variant="filled"
                        />
                      </TableCell>
                      <TableCell>{transaction.chainId}</TableCell>
                      <TableCell>
                        {transaction.txHash && transaction.txHash !== 'none' && transaction.txHash !== 'null' ? (
                          <Button
                            size="small"
                            variant="text"
                            href={getSnowtraceUrl(transaction.txHash)}
                            target="_blank"
                            rel="noopener noreferrer"
                            endIcon={<OpenInNewIcon fontSize="small" />}
                            sx={{ fontFamily: 'monospace', fontSize: '0.8rem', textTransform: 'none' }}
                          >
                            {formatAddress(transaction.txHash)}
                          </Button>
                        ) : (
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem', color: 'text.secondary' }}>
                            Pending
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            User: {formatAddress(transaction.userWallet)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            System: {formatAddress(transaction.systemWallet)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(transaction.createdAt)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <IconButton
                            size="small"
                            onClick={() => handleStatusUpdate(transaction)}
                            title="Edit Transaction"
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleResetTransaction(transaction.id)}
                            title="Reset Transaction (set retry count to 0 and status to PENDING)"
                            color="warning"
                            disabled={resetTransactionMutation.isLoading}
                          >
                            <RestartAltIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteTransaction(transaction.id)}
                            title="Delete Transaction"
                            color="error"
                            disabled={deleteTransactionMutation.isLoading}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <Box display="flex" justifyContent="center" mt={3}>
                <Pagination
                  count={pagination.pages}
                  page={pagination.page}
                  onChange={(_, newPage) => setPage(newPage)}
                  color="primary"
                />
              </Box>
            )}
          </>
        )}
      </Paper>

      {/* Edit Transaction Dialog */}
      <Dialog open={statusDialogOpen} onClose={() => setStatusDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box>
            <Typography variant="h6">Edit Transaction</Typography>
            {selectedTransaction && (
              <Box sx={{ mt: 1 }}>
                <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                  <Typography variant="body2" color="text.secondary">
                    ID: {selectedTransaction.id}
                  </Typography>
                  {selectedTransaction.txHash && selectedTransaction.txHash !== 'none' && selectedTransaction.txHash !== 'null' && (
                    <Button
                      size="small"
                      variant="outlined"
                      href={getSnowtraceUrl(selectedTransaction.txHash)}
                      target="_blank"
                      rel="noopener noreferrer"
                      endIcon={<OpenInNewIcon />}
                      sx={{ textTransform: 'none' }}
                    >
                      View on Snowtrace
                    </Button>
                  )}
                  {selectedTransaction.userId && (
                    <Button
                      size="small"
                      variant="outlined"
                      href={getUserUrl(selectedTransaction.userId)}
                      target="_blank"
                      rel="noopener noreferrer"
                      startIcon={<PersonIcon />}
                      sx={{ textTransform: 'none' }}
                    >
                      View User
                    </Button>
                  )}
                </Stack>
              </Box>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              {/* Basic Info */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>Basic Information</Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={editForm.status}
                    label="Status"
                    onChange={(e) => setEditForm(prev => ({ ...prev, status: e.target.value }))}
                  >
                    <MenuItem value="PENDING">Pending</MenuItem>
                    <MenuItem value="PENDING_APPROVAL">Pending Approval</MenuItem>
                    <MenuItem value="APPROVED">Approved</MenuItem>
                    <MenuItem value="PROCESSING">Processing</MenuItem>
                    <MenuItem value="CONFIRMED">Confirmed</MenuItem>
                    <MenuItem value="PROCESSED">Processed</MenuItem>
                    <MenuItem value="FAILED">Failed</MenuItem>
                    <MenuItem value="CANCELLED">Cancelled</MenuItem>
                    <MenuItem value="BANNED_USER_REVIEW">Banned User Review</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Direction</InputLabel>
                  <Select
                    value={selectedTransaction?.direction || ''}
                    label="Direction"
                    disabled
                  >
                    <MenuItem value="DEPOSIT">Deposit</MenuItem>
                    <MenuItem value="WITHDRAWAL">Withdrawal</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Amount (Wei)"
                  value={editForm.amount}
                  onChange={(e) => setEditForm(prev => ({ ...prev, amount: e.target.value }))}
                  helperText={`${formatAmount(editForm.amount)} MOANI`}
                  sx={{ fontFamily: 'monospace' }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Chain ID"
                  value={editForm.chainId}
                  onChange={(e) => setEditForm(prev => ({ ...prev, chainId: e.target.value }))}
                  type="number"
                />
              </Grid>
              
              {/* Transaction Details */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>Transaction Details</Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Transaction Hash"
                  value={editForm.txHash}
                  onChange={(e) => setEditForm(prev => ({ ...prev, txHash: e.target.value }))}
                  helperText={editForm.txHash && editForm.txHash !== 'none' && editForm.txHash !== 'null' ? (
                    <Button
                      size="small"
                      href={getSnowtraceUrl(editForm.txHash)}
                      target="_blank"
                      rel="noopener noreferrer"
                      endIcon={<OpenInNewIcon />}
                      sx={{ textTransform: 'none', p: 0, minWidth: 'auto' }}
                    >
                      View on Snowtrace
                    </Button>
                  ) : null}
                  sx={{ fontFamily: 'monospace' }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Block Number"
                  value={editForm.blockNumber}
                  onChange={(e) => setEditForm(prev => ({ ...prev, blockNumber: e.target.value }))}
                  type="number"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Confirmation Blocks"
                  value={editForm.confirmationBlocks}
                  onChange={(e) => setEditForm(prev => ({ ...prev, confirmationBlocks: parseInt(e.target.value) || 0 }))}
                  type="number"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Required Confirmations"
                  value={editForm.requiredConfirmations}
                  onChange={(e) => setEditForm(prev => ({ ...prev, requiredConfirmations: parseInt(e.target.value) || 12 }))}
                  type="number"
                />
              </Grid>
              
              {/* Retry Settings */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>Retry Settings</Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Retry Count"
                  value={editForm.retryCount}
                  onChange={(e) => setEditForm(prev => ({ ...prev, retryCount: parseInt(e.target.value) || 0 }))}
                  type="number"
                  inputProps={{ min: 0 }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Max Retries"
                  value={editForm.maxRetries}
                  onChange={(e) => setEditForm(prev => ({ ...prev, maxRetries: parseInt(e.target.value) || 3 }))}
                  type="number"
                  inputProps={{ min: 0 }}
                />
              </Grid>
              
              {/* Wallet Addresses */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>Wallet Addresses</Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="User Wallet"
                  value={editForm.userWallet}
                  onChange={(e) => setEditForm(prev => ({ ...prev, userWallet: e.target.value }))}
                  sx={{ fontFamily: 'monospace' }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="System Wallet"
                  value={editForm.systemWallet}
                  onChange={(e) => setEditForm(prev => ({ ...prev, systemWallet: e.target.value }))}
                  sx={{ fontFamily: 'monospace' }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Contract Address"
                  value={editForm.contractAddress}
                  onChange={(e) => setEditForm(prev => ({ ...prev, contractAddress: e.target.value }))}
                  sx={{ fontFamily: 'monospace' }}
                />
              </Grid>
              
              {/* Error Message */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>Error Information</Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Error Message"
                  multiline
                  rows={4}
                  value={editForm.errorMessage}
                  onChange={(e) => setEditForm(prev => ({ ...prev, errorMessage: e.target.value }))}
                  placeholder="Error message if transaction failed..."
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleSubmitStatusUpdate} 
            variant="contained"
            disabled={updateTransactionMutation.isLoading}
          >
            {updateTransactionMutation.isLoading ? <CircularProgress size={20} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Status Update Dialog */}
      <Dialog open={bulkStatusDialogOpen} onClose={() => setBulkStatusDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Bulk Update Transaction Status
          <Typography variant="body2" color="text.secondary">
            {selectedTransactions.length} transactions selected
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusForm.status}
                label="Status"
                onChange={(e) => setStatusForm(prev => ({ ...prev, status: e.target.value }))}
              >
                <MenuItem value="PENDING">Pending</MenuItem>
                <MenuItem value="CONFIRMED">Confirmed</MenuItem>
                <MenuItem value="PROCESSED">Processed</MenuItem>
                <MenuItem value="FAILED">Failed</MenuItem>
                <MenuItem value="CANCELLED">Cancelled</MenuItem>
              </Select>
            </FormControl>
            
            {statusForm.status === 'FAILED' && (
              <TextField
                fullWidth
                label="Error Message"
                multiline
                rows={3}
                value={statusForm.errorMessage}
                onChange={(e) => setStatusForm(prev => ({ ...prev, errorMessage: e.target.value }))}
                sx={{ mb: 2 }}
              />
            )}
            
            <TextField
              fullWidth
              label="Notes (Optional)"
              multiline
              rows={2}
              value={statusForm.notes}
              onChange={(e) => setStatusForm(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Add any notes about this bulk status change..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkStatusDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleSubmitBulkStatusUpdate} 
            variant="contained"
            disabled={bulkUpdateStatusMutation.isLoading}
          >
            {bulkUpdateStatusMutation.isLoading ? <CircularProgress size={20} /> : `Update ${selectedTransactions.length} Transactions`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default MoaniBlockchainTransfers;
